import React, { lazy, Suspense, useEffect } from 'react';
import PropTypes from 'prop-types';

import { usePrefs } from 'ws-scripts/modules/common';

import vehiclePropType from '../../../propTypes/vehiclePropType';
import MediaFocusedCarouselContainer from '../mediaSection/MediaFocusedCarouselContainer';
import DetailsContainer from '../detailsSection/DetailsContainer';
import FooterContainer from '../footerSection/FooterContainer';
import VehicleCardContainer from './common/VehicleCardContainer';
import Callout from '../Callout';

const LazyImage = lazy(() =>
	import(
		/* webpackChunkName: 'wsmVehicleMediaGalleryImage' */ 'wsm-vehicle-media-gallery'
	).then((module) => ({
		default: module.Image
	}))
);

const ListCard = ({
	accounts,
	featuredPromotion,
	forwardedRef,
	incentives,
	inventoryIndex,
	setShowOffSite,
	shouldFullyRenderCard,
	shouldShowCompare,
	vehicle
}) => {
	const { showSingleImgForMediaGallery } = usePrefs();

	const canRender = shouldFullyRenderCard && !vehicle.isPlaceholder;
	const {
		uuid,
		link,
		images = [],
		featuredPromotion: featuredPromotionAttr = false,
		offSite
	} = vehicle;

	useEffect(() => {
		import('./ListCard.scss');
	}, []);

	return (
		<VehicleCardContainer // Suggestion: added this so it's reuseable for GridCard
			inventoryIndex={inventoryIndex}
			setShowOffSite={setShowOffSite}
			isFeaturedPromotion={featuredPromotion || featuredPromotionAttr}
			isOffsite={offSite}
			forwardedRef={forwardedRef}
			layout="list"
			uuid={uuid}
		>
			<div className="vehicle-card-body ddc-font-size-small d-sm-flex flex-sm-row">
				{showSingleImgForMediaGallery === 'true' ? (
					<div className="vehicle-card-media-container gallery-list-card-single-image">
						<div className="img-container img-loaded">
							{images.length > 0 && (
								<Suspense>
									<LazyImage
										image={images[0]}
										shouldFullyRenderCard={
											shouldFullyRenderCard
										}
									/>
								</Suspense>
							)}
						</div>
					</div>
				) : (
					<MediaFocusedCarouselContainer
						images={images}
						shouldFullyRenderCard={shouldFullyRenderCard}
					/>
				)}
				<div className="vehicle-details-wrapper">
					<DetailsContainer
						vehicle={vehicle}
						incentives={incentives}
						accounts={accounts}
						shouldFullyRenderCard={canRender}
					/>
					<div className="d-flex">
						<Callout callouts={vehicle.callout} vehicle={vehicle} />
					</div>
					<FooterContainer
						shouldShowCompare={shouldShowCompare}
						vehicle={vehicle}
						uuid={uuid}
						link={link}
						shouldFullyRenderCard={canRender}
					/>
				</div>
			</div>
		</VehicleCardContainer>
	);
};

const WrappedListCard = React.forwardRef((props, ref) => {
	return <ListCard {...props} forwardedRef={ref} />;
});

WrappedListCard.displayName = 'ListCard';

ListCard.propTypes = {
	accounts: PropTypes.shape({}).isRequired,
	forwardedRef: PropTypes.oneOfType([
		PropTypes.func,
		PropTypes.shape({ current: PropTypes.instanceOf(Element) })
	]),
	featuredPromotion: PropTypes.bool,
	incentives: PropTypes.shape({}).isRequired,
	inventoryIndex: PropTypes.number.isRequired,
	setShowOffSite: PropTypes.func,
	shouldFullyRenderCard: PropTypes.bool,
	shouldShowCompare: PropTypes.bool.isRequired,
	vehicle: vehiclePropType.isRequired
};

export default WrappedListCard;
